import { listStages } from "../../content/stages.js";
import { useState } from "react";
import type { AnalyticsSummary } from "./analyticsApi.js";
import { HourlyDetailPanel } from "./HourlyDetailPanel.js";
import { chartTickIndices } from "./chartTicks.js";
import { stageColor } from "./stageColors.js";

export type HourlyMetric = "stages" | "players";
export type HourlyBucket = { at: number; hour: string; stages: Map<string, number>; playerCount: number };

type Props = {
  from: number;
  to: number;
  stageRows: AnalyticsSummary["hourly"];
  playerRows: AnalyticsSummary["hourlyPlayers"];
  metric: HourlyMetric;
};

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function hourKey(milliseconds: number): string {
  return new Date(milliseconds + JST_OFFSET_MS).toISOString().slice(0, 13) + ":00:00";
}

function bucketLabel(hour: string): string {
  return `${Number(hour.slice(8, 10))}日${Number(hour.slice(11, 13))}時`;
}

function buildBuckets(from: number, to: number, stageRows: Props["stageRows"], playerRows: Props["playerRows"]): HourlyBucket[] {
  const stageByHour = new Map<string, Map<string, number>>();
  for (const row of stageRows) {
    const values = stageByHour.get(row.hour) ?? new Map<string, number>();
    values.set(row.stageId, row.runCount);
    stageByHour.set(row.hour, values);
  }
  const playersByHour = new Map(playerRows.map((row) => [row.hour, row.playerCount]));
  const first = Math.floor(from / 3_600_000) * 3_600_000;
  return Array.from({ length: Math.ceil((to - first) / 3_600_000) }, (_, index) => {
    const at = first + index * 3_600_000;
    const hour = hourKey(at);
    return { at, hour, stages: stageByHour.get(hour) ?? new Map<string, number>(), playerCount: playersByHour.get(hour) ?? 0 };
  });
}

export function HourlyChart({ from, to, stageRows, playerRows, metric }: Props) {
  const [selected, setSelected] = useState<number | null>(null);
  const stages = listStages();
  const buckets = buildBuckets(from, to, stageRows, playerRows);
  const total = (bucket: HourlyBucket) => metric === "stages"
    ? [...bucket.stages.values()].reduce((sum, count) => sum + count, 0)
    : bucket.playerCount;
  const chartHeight = 176;
  const plotTop = 14;
  const plotBottom = 34;
  const plotHeight = chartHeight - plotTop - plotBottom;
  const max = Math.max(1, ...buckets.map(total));
  const step = Math.max(30, 276 / Math.max(1, buckets.length));
  const width = buckets.length * step + 84;
  const tickIndices = chartTickIndices(buckets.length, step);
  const selectedBucket = selected === null ? null : buckets.find((bucket) => bucket.at === selected) ?? null;

  return (
    <div className="analytics-hourly">
      <div className="analytics-chart-scroll">
        <svg className="analytics-hourly-chart" style={{ width: Math.max(360, width) }} viewBox={`0 0 ${width} ${chartHeight}`} role="img" aria-label={metric === "stages" ? "時間別ラン開始数。色はステージごとの内訳です。" : "時間別ユニークプレイヤー数。"}>
          <line className="analytics-chart-axis" x1="38" x2={width - 4} y1={plotTop + plotHeight} y2={plotTop + plotHeight} />
          <text className="analytics-chart-tick" x="32" y={plotTop + 4} textAnchor="end">{max}</text>
          {buckets.map((bucket, index) => {
            const x = 42 + index * step;
            let heightFromBottom = 0;
            const label = `${bucket.hour.replace("T", " ")}の内訳`;
            return (
              <g
                key={bucket.at}
                role="button"
                aria-label={label}
                aria-pressed={selected === bucket.at}
                className={selected === bucket.at ? "analytics-bar is-selected" : "analytics-bar"}
                onClick={() => setSelected(selected === bucket.at ? null : bucket.at)}
              >
                <title>{metric === "stages"
                  ? `${bucket.hour.replace("T", " ")}：${stages.map((stage) => `${stage.name} ${bucket.stages.get(stage.id) ?? 0}`).join("、")}`
                  : `${bucket.hour.replace("T", " ")}：ユニークプレイヤー ${bucket.playerCount}`}</title>
                <rect className="analytics-bar-hit" x={x - 6} y={plotTop} width="30" height={plotHeight} />
                {metric === "stages" ? stages.map((stage, stageIndex) => {
                  const count = bucket.stages.get(stage.id) ?? 0;
                  const height = plotHeight * count / max;
                  const y = plotTop + plotHeight - heightFromBottom - height;
                  heightFromBottom += height;
                  return <rect key={stage.id} fill={stageColor(stageIndex)} x={x} y={y} width="18" height={height} rx="2" />;
                }) : <rect className="analytics-bar-player" x={x} y={plotTop + plotHeight - plotHeight * bucket.playerCount / max} width="18" height={plotHeight * bucket.playerCount / max} rx="2" />}
                {tickIndices.has(index) && (
                  <text className="analytics-chart-label" x={x + 9} y={chartHeight - 13} textAnchor="middle">
                    {bucketLabel(bucket.hour)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      {metric === "stages" && <div className="analytics-stage-legend">{stages.map((stage, index) => <span key={stage.id}><i style={{ background: stageColor(index) }} />{stage.name}</span>)}</div>}
      {selectedBucket && <HourlyDetailPanel bucket={selectedBucket} metric={metric} onClose={() => setSelected(null)} />}
    </div>
  );
}
